import { useMemo, useState } from 'react';
import { Brand } from '../../lib/types';
import { BrandAvatar } from '../../lib/brandAvatars';

interface Props {
  brands: Brand[];
  selected: string[];
  onToggle: (brandId: string) => void;
  /** Kept for callers; the picker is always alphabetical now that the
   *  brand priority (A/B/C) is not surfaced (Doreen, 2026-10-12). */
  alphabetical?: boolean;
}

/** Brand-chip multi-select: selected brands first, then alphabetical, with
 *  a small search box (200+ brands since the catalog import, 2026-10-12).
 *  Selected brands always stay visible, even when they don't match. */
export const BrandChipPicker = ({ brands, selected, onToggle }: Props) => {
  const [query, setQuery] = useState('');
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const sorted = useMemo(
    () =>
      [...brands].sort((a, b) => {
        const aSel = selectedSet.has(a.id) ? 0 : 1;
        const bSel = selectedSet.has(b.id) ? 0 : 1;
        if (aSel !== bSel) return aSel - bSel;
        return a.name.localeCompare(b.name);
      }),
    [brands, selectedSet],
  );

  const q = query.trim().toLowerCase();
  const visible = q
    ? sorted.filter((b) => selectedSet.has(b.id) || b.name.toLowerCase().includes(q))
    : sorted;

  return (
    <>
      <input
        className="input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search brands…"
        style={{ marginBottom: 8, fontSize: 12.5 }}
      />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 180, overflowY: 'auto' }}>
      {visible.map((b) => {
        const active = selectedSet.has(b.id);
        return (
          <button
            key={b.id}
            type="button"
            className={`brand-chip ${active ? 'active' : ''}`}
            onClick={() => onToggle(b.id)}
          >
            <BrandAvatar brand={b} size={18} />
            {b.name}
          </button>
        );
      })}
      {visible.length === 0 && (
        <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>No brand matches "{query.trim()}".</span>
      )}
      </div>
    </>
  );
};

export default BrandChipPicker;
